import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";


import Pagination from "modules/appointments/components/Pagination";
import StatusSelect from "modules/appointments/components/StatusSelect";
import {
  STATUS_LABEL,
  STATUS_TONE,
  END_STATUSES,
} from "modules/appointments/helper/appointmentConstants";
import { formatDate, formatTimeSlot, formatGenderAge } from "modules/appointments/helper/formatters";
import {
  PROFILE_TYPE_CHIP_CLASS,
  profileTypeLabel,
} from "modules/patient/helper/patientConstants";

import MobileStatusRow from "../components/MobileStatusRow";
import useVisualViewport from "../useVisualViewport";
import useDragScroll from "../useDragScroll";
import MobileAccountMenu from "../components/MobileAccountMenu";
import MobileFilterSheet from "../components/MobileFilterSheet";
import "../styles/mobile.scss";

/**
 * "My appointments" on a phone — Figma 5868:3607, with the filter sheet at
 * 5878:2.
 *
 * Renders INSTEAD of the desktop table, from values AppointmentsDashboard has
 * already computed. It fetches nothing and filters nothing itself: every
 * control here calls the same `searchBy` / `setColumnFilter` / `changeStatus`
 * the desktop controls call, with the same arguments.
 *
 * Three differences from desktop, all asked for:
 *
 *  - NO "Book appointment". The board does not carry it; the route still
 *    exists and desktop still offers it.
 *  - THE WHOLE CARD OPENS THE PATIENT. Desktop deliberately keeps its row inert
 *    and navigates only from the two id links, because a wide row's cells are
 *    each worth reading on their own. A card is one object under one thumb, so
 *    the ids stop being links and the card becomes the target.
 *  - THE TWO SEARCH BOXES AND SIX FUNNELS COLLAPSE into a status strip plus one
 *    sheet. Same filters, one surface.
 */

/** Where the card goes. Keyed by the APPOINTMENT id — App.js routes the patient
 *  screen as /navigo-module/patient/:appointmentId, which is the only id that
 *  retrieves both the appointment and the patient it points at. The card
 *  DISPLAYS a patient id; the two must not be reconciled. */
const patientRoute = (a) => `/navigo-module/patient/${a.id}`;

const MobileAppointmentsScreen = ({
  appointments,
  totalCount,
  loading,
  scope,
  filters,
  ui,
  onColumnFilter,
  appointmentIdInput,
  patientIdInput,
  onSearchBy,
  onSetAppointmentIdInput,
  onSetPatientIdInput,
  updatingId,
  onChangeStatus,
  onGoToPage,
  onOpenPatient,
  loadServices,
  loadAccounts,
  dialogs,
}) => {
  /* Keeps an open keyboard from burying the bottom of the filter sheet. */
  useVisualViewport();
  /* Mouse drag pans, for driving this layout in a narrow desktop window. */
  useDragScroll();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);

  const onLoadServices = useCallback(() => {
    if (!loadServices) return;
    loadServices("", (opts) => setServiceOptions(opts || []));
  }, [loadServices]);


  /* Any filter beyond the default page/sort — drives the dot on the sheet
     button. Read from `ui`, which is what the funnels themselves read. */
  const filtersActive = Boolean(
    (ui && (ui.status || ui.appointmentDate || ui.service || ui.accountCode || ui.userType)) ||
      filters.appointmentId ||
      filters.patientId
  );

  const applyFilters = (draft) => {
    setFiltersOpen(false);
    /* One call per control, each the same call the desktop control makes.
       Order does not matter: they are independent keys on one filter object,
       and each already resets to page 1. */
    onSetAppointmentIdInput(draft.appointmentId || "");
    onSearchBy("appointmentId", draft.appointmentId || "");
    onSetPatientIdInput(draft.patientId || "");
    onSearchBy("patientId", draft.patientId || "");
    onColumnFilter("status", draft.status && draft.status.length ? draft.status : null);
    onColumnFilter(
      "appointmentDate",
      draft.from || draft.to ? { from: draft.from || "", to: draft.to || "" } : null
    );
    /* An array, multi-select, as the desktop funnel sends it. */
    onColumnFilter("service", draft.service && draft.service.length ? draft.service : null);
    onColumnFilter("userType", draft.userType && draft.userType.length ? draft.userType : null);
    /* Already an array — ColumnFilter is multi-select, exactly as on desktop. */
    onColumnFilter("accountCode", draft.accountCode && draft.accountCode.length ? draft.accountCode : null);
  };

  const resetFilters = () => {
    setFiltersOpen(false);
    onSetAppointmentIdInput("");
    onSearchBy("appointmentId", "");
    onSetPatientIdInput("");
    onSearchBy("patientId", "");
    ["status", "appointmentDate", "service", "accountCode", "userType"].forEach((c) =>
      onColumnFilter(c, null)
    );
  };

  const first = totalCount === 0 ? 0 : (filters.pageNum - 1) * filters.pageSize + 1;
  const last = Math.min(filters.pageNum * filters.pageSize, totalCount);

  return (
    <div className="zym-appts">
      <header className="zym-appts-head">
        <h1 className="zym-appts-title">My appointments</h1>
        {/* This screen renders no TopBar, so without the account menu here
            there is no way to log out of the screen you land on. */}
        {/* The avatar alone, and a panel that says whose account it is before
            it offers to end the session. Desktop's UserMenu is a name chip,
            which does not fit beside the title at 360px. */}
        <MobileAccountMenu />
      </header>

      <MobileStatusRow
        value={ui && ui.status}
        onPick={(ids) => onColumnFilter("status", ids)}
        onOpenFilters={() => setFiltersOpen(true)}
        filtersActive={filtersActive}
      />

      <div className="zym-appts-body">
        {(loading || scope.state === "resolving") && (
          <div className="zym-message">Loading appointments&hellip;</div>
        )}

        {/* The four causes of an empty list, each said plainly — desktop's
            wording, kept identical so the two screens cannot drift. */}
        {scope.state === "none" && (
          <div className="zym-message is-note">
            This screen shows appointments for the provider you are signed in as,
            and your account isn&rsquo;t linked to a provider profile &mdash; so there
            is nothing to list. Ask an admin to link your doctor profile to a
            service provider.
          </div>
        )}

        {scope.state === "error" && (
          <div className="zym-message is-note">
            Couldn&rsquo;t confirm which provider you are signed in as, so no
            appointments are shown &mdash; rather than showing everyone&rsquo;s.
            Reload to try again.
          </div>
        )}

        {!loading && scope.state === "ok" && appointments.length === 0 && totalCount === 0 && (
          <div className="zym-message">No appointment found!</div>
        )}

        {!loading && scope.state === "ok" && appointments.length === 0 && totalCount > 0 && (
          <div className="zym-message">
            Nothing on this page &mdash; the list got shorter. Pick a page below.
          </div>
        )}

        {!loading && totalCount > 0 && appointments.length > 0 && (
          <p className="zym-appts-count">
            Showing {first}&ndash;{last} of {totalCount}
          </p>
        )}

        {!loading &&
          appointments.map((a) => {
            const typeLabel = profileTypeLabel(a.patientProfileType);
            return (
              <div className="zym-card" key={a.id}>
                {/* The tap target is a real button covering the card, not an
                    onClick on the card div: the card holds a live status
                    dropdown, and nesting that inside a button would make every
                    attempt to change status also open the patient. This sits
                    UNDER the content (z-index) so the dropdown still wins its
                    own taps, and carries the accessible name for the whole
                    card. */}
                <button
                  type="button"
                  className="zym-card-hit"
                  /* The name rather than child text: the card's own text is
                     pointer-events: none so taps reach this layer, and putting
                     a visually-hidden label inside would mean borrowing a
                     screen-reader class from another module's stylesheet. */
                  aria-label={`Open ${a.patientName || "patient"}'s record`}
                  onClick={() => onOpenPatient(patientRoute(a))}
                />

                <div className="zym-card-top">
                  <div className="zym-card-who">
                    <span className="zym-card-name">{a.patientName}</span>
                    <span className="zym-card-age">
                      {formatGenderAge(a.patientGender, a.patientAge)}
                    </span>
                    {/* Only when the row actually carries a type. Desktop says
                        this with a coloured avatar; a word survives at 360px
                        and is readable by assistive tech, which the colour was
                        not — but it should carry the SAME colour, so it wears
                        the header's chip classes rather than the avatar's. The
                        avatar tones cannot be borrowed: they are nested under
                        `.avatar`, which also paints a person icon behind them
                        (appointments.scss:294-310). */}
                    {typeLabel && (
                      <span
                        className={`ph-chip ${
                          PROFILE_TYPE_CHIP_CLASS[a.patientProfileType] || ""
                        }`}
                      >
                        {typeLabel}
                      </span>
                    )}
                  </div>

                  {/* Above the hit layer, so the dropdown opens rather than
                      navigating — the same carve-out desktop makes for this
                      cell, and for the same reason: it starts the cancel/refund
                      flow, which navigating away would abandon. */}
                  <div className="zym-card-status">
                    <StatusSelect
                      value={a.status}
                      tone={STATUS_TONE[a.status] || "progress"}
                      label={STATUS_LABEL[a.status] || "—"}
                      labels={STATUS_LABEL}
                      tones={STATUS_TONE}
                      disabled={updatingId === a.id}
                      readOnly={END_STATUSES.indexOf(a.status) !== -1}
                      onChange={(next) => onChangeStatus(a, next)}
                    />
                  </div>
                </div>

                <p className="zym-card-when">
                  {formatDate(a.appointmentDate)} &middot;{" "}
                  {formatTimeSlot(a.timeSlot, a.serviceDuration)}
                </p>

                <p className="zym-card-what">
                  {[a.serviceName, a.accountCode].filter(Boolean).join(" · ")}
                </p>

                {/* PLAIN TEXT, not links. The card is the target now, and a
                    link inside it would be a second, smaller one that went
                    somewhere else. Same family-booking rule as desktop:
                    `bookedFor` is the member seen, `patientID` the account
                    holder who booked. */}
                <p className="zym-card-ids">
                  AID {a.id} &middot; PID{" "}
                  {a.bookedFor && a.bookedFor !== 0 ? a.bookedFor : a.patientID}
                </p>
              </div>
            );
          })}

        {!loading && totalCount > 0 && (
          <Pagination
            pageNum={filters.pageNum}
            pageSize={filters.pageSize}
            totalCount={totalCount}
            onChange={onGoToPage}
          />
        )}
      </div>

      <MobileFilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        appointmentId={appointmentIdInput}
        patientId={patientIdInput}
        status={ui && ui.status}
        dateRange={ui && ui.appointmentDate}
        service={ui && ui.service}
        serviceOptions={serviceOptions}
        onLoadServices={onLoadServices}
        loadServices={loadServices}
        userType={ui && ui.userType}
        accountCode={ui && ui.accountCode}
        /* Straight through: the mounted funnel does its own fetching and its
           own server-side search, so nothing is pre-read here. */
        onLoadAccounts={loadAccounts}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      {dialogs}
    </div>
  );
};

MobileAppointmentsScreen.propTypes = {
  appointments: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  scope: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  /** What the funnels show — the popover-language half of the filter state. */
  ui: PropTypes.object,
  onColumnFilter: PropTypes.func.isRequired,
  appointmentIdInput: PropTypes.string,
  patientIdInput: PropTypes.string,
  onSearchBy: PropTypes.func.isRequired,
  onSetAppointmentIdInput: PropTypes.func.isRequired,
  onSetPatientIdInput: PropTypes.func.isRequired,
  updatingId: PropTypes.number,
  onChangeStatus: PropTypes.func.isRequired,
  onGoToPage: PropTypes.func.isRequired,
  onOpenPatient: PropTypes.func.isRequired,
  loadServices: PropTypes.func,
  loadAccounts: PropTypes.func,
  dialogs: PropTypes.node,
};

export default MobileAppointmentsScreen;
